<template>
    <div class="mt-4 border-b border-sidebar-border">
        <div v-if="$page.props?.auth?.user">
            <div
                @click="$emit('toggleProfileMenu')"
                :class="[
                    padding,
                    'relative py-6 overflow-hidden flex flex-col gap-y-4 underline-hover transition-colors duration-button-hover cursor-pointer',
                ]"
            >
                <div
                    class="h-16 w-16 rounded-full border border-border overflow-hidden flex justify-center items-center"
                >
                    <img
                        v-if="
                            $page.props?.auth?.user.picturePath &&
                            !hadImageError
                        "
                        class="w-full h-full bg-gray-50 object-cover flex-shrink-0"
                        :src="$page.props?.auth?.user.picturePath"
                        @error="() => (hadImageError = true)"
                        alt=""
                    />
                    <PhUser v-else class="text-user-accent bg-user size-full" />
                </div>
                <div>
                    <p
                        class="text-ellipsis overflow-hidden font-medium md:font-normal"
                        aria-hidden="true"
                        v-text="$page.props?.auth?.user.name"
                    />
                    <p
                        v-if="$page.props?.auth?.user.typeRole"
                        class="text-sm italic text-ellipsis overflow-hidden text-gray-400"
                        v-text="$page.props?.auth?.user.typeRole"
                    />
                </div>

                <div class="absolute right-3 top-1/2 -translate-y-1/2">
                    <PhCaretDown
                        :class="[
                            { '[transform:rotateX(180deg)]': profileMenuOpen },
                            'w-6 flex-shrink-0 transition-transform translate-x-',
                        ]"
                    />
                </div>
                <!-- New chat message notification -->
                <Transition name="fade">
                    <Link
                        @click="onRouteToUnreadChat"
                        v-if="showUnreadChatUrl"
                        :href="unreadChatUrl"
                        class="absolute w-fit left-28 top-2 md:top-5 md:left-auto md:right-2 rounded-full border border-transparent hover:border-border transition-colors duration-button-hover p-2"
                    >
                        <div class="relative">
                            <PhChatCircle class="size-6" />
                            <div
                                class="absolute -right-1 top-0 size-3 rounded-full bg-blue-50 z-0"
                            >
                                <div class="relative size-full">
                                    <div
                                        class="inset-0 absolute bg-blue-500 animate-pulse size-full rounded-full"
                                    />
                                </div>
                            </div>
                        </div>
                    </Link>
                </Transition>
            </div>
            <VerticalSlideTransition :show="profileMenuOpen" :keepAlive="true">
                <div class="flex flex-col">
                    <template v-for="(item, i) in profileItems">
                        <DynamicLink
                            v-if="item.route"
                            :href="item.route"
                            @click="() => $emit('linkClicked', item)"
                        >
                            <div
                                :class="[
                                    { 'border-t': i == 0 },
                                    {
                                        'bg-sidebar-active text-sidebar-active-text':
                                            item.active,
                                    },
                                    linkClasses,
                                ]"
                            >
                                <p v-text="item.text"></p>
                                <PhArrowRight class="w-6" />
                            </div>
                        </DynamicLink>
                    </template>
                    <button @click="logout" :class="linkClasses">
                        <p v-text="$lang.get('Log out')"></p>
                        <PhArrowRight class="w-6" />
                    </button>
                </div>
            </VerticalSlideTransition>
        </div>
    </div>
</template>
<script>
import DynamicLink from '@/Components/Utilities/DynamicLink.vue'
import { Link, router } from '@inertiajs/vue3'
import {
    PhCaretDown,
    PhArrowRight,
    PhUser,
    PhChatCircle,
} from '@phosphor-icons/vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'

export default {
    components: {
        VerticalSlideTransition,
        PhCaretDown,
        PhArrowRight,
        PhChatCircle,
        PhUser,
        Link,
        DynamicLink,
    },

    props: {
        profileMenuOpen: {
            type: Boolean,
            required: true,
        },
        profileItems: {
            type: Array,
            required: true,
        },
    },

    emits: ['linkClicked', 'toggleProfileMenu'],

    data: () => ({
        hadImageError: false,
        echoChannel: null,
        removeNavigationListener: null,
        currentRouteIsChat: false,
        userChatStatusEvent: null,
    }),

    beforeCreate() {
        this.padding = 'pl-11 pr-6'
        this.linkClasses =
            'underline-hover transition-colors duration-button-hover w-full justify-between text-left pl-14 p-4 border-b border-sidebar-border text-md flex gap-x-2'
    },

    created() {
        this.initEcho()
        this.initNavigationListener()
    },

    beforeUnmount() {
        this.destroyEcho()
        this.destroyNavigationListener()
    },

    computed: {
        notificationChannel() {
            return `App.Models.User.${this.$page.props.auth.user.id}`
        },

        showUnreadChatUrl() {
            return !this.currentRouteIsChat && this.unreadChatUrl
        },

        unreadChatUrl() {
            if (!this.userChatStatusEvent?.participants?.length) {
                return null
            }
            const firstUnreadParticipant =
                this.userChatStatusEvent.participants.find(
                    (participant) => participant.unread
                )

            return firstUnreadParticipant?.chat_url || null
        },
    },

    methods: {
        logout() {
            router.post(route('logout'))
        },

        initEcho() {
            if (this.$page.props.auth.user) {
                this.echoChannel = window.Echo.private(this.notificationChannel)

                this.echoChannel.listen('UserChatStatusEvent', (event) => {
                    this.userChatStatusEvent = event
                })
            }
        },

        destroyEcho() {
            if (this.$page.props?.auth?.user) {
                this.echoChannel = window.Echo.leaveChannel(
                    this.notificationChannel
                )

                this.echoChannel = null
            }
        },

        initNavigationListener() {
            this.removeNavigationListener = router.on(
                'navigate',
                this.onNavigation
            )
        },
        destroyNavigationListener() {
            if (this.removeNavigationListener) {
                this.removeNavigationListener()
            }
        },

        onNavigation() {
            this.currentRouteIsChat = route().current('chats.index')
        },

        onRouteToUnreadChat() {
            // If there are no unread participants, return
            if (!this.userChatStatusEvent?.participants?.length) {
                return
            }

            const firstUnreadParticipant =
                this.userChatStatusEvent.participants.find(
                    (participant) => participant.unread
                )

            // If there are no unread participants, return
            if (!firstUnreadParticipant) {
                return
            }

            // Remove this participant from the unread list by id and model_class
            this.userChatStatusEvent.participants =
                this.userChatStatusEvent.participants.filter(
                    (p) =>
                        p.id !== firstUnreadParticipant.id ||
                        p.model_class !== firstUnreadParticipant.model_class
                )
        },
    },
}
</script>
